import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/app/context/AuthContext";
import { ChesslaLoadingMark } from "@/shared/components/ChesslaLoadingMark";
import { loginUrl } from "@/features/auth/auth-navigation";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { useUserSettingsStore } from "@/features/preferences/user-settings.store";
import { ENTITLEMENTS_INVALIDATED_EVENT } from "@/shared/api/entitlement-events";

/**
 * Layout wrapper for authenticated routes. Uses the AuthProvider context
 * to check authentication state instead of directly calling fetchAuthSession.
 * This allows proper logout by clearing the context state.
 */
export const AuthLayout = () => {
  const { isAuthenticated, isLoading, userId } = useAuth();
  const location = useLocation();
  const loadEntitlements = useEntitlementsStore((state) => state.load);
  const refreshEntitlements = useEntitlementsStore((state) => state.refresh);
  const nextGrantAt = useEntitlementsStore((state) => state.snapshot?.membership.nextGrantAt);
  const loadUserSettings = useUserSettingsStore((state) => state.load);
  const settingsOwnerUserId = useUserSettingsStore((state) => state.ownerUserId);
  const settingsLoading = useUserSettingsStore((state) => state.loading);

  useEffect(() => {
    void loadEntitlements(isAuthenticated ? userId : null);
  }, [isAuthenticated, loadEntitlements, userId]);
  useEffect(() => {
    void loadUserSettings(isAuthenticated ? userId : null).catch(() => undefined);
  }, [isAuthenticated, loadUserSettings, userId]);
  useEffect(() => {
    const refresh = () => {
      void refreshEntitlements();
    };
    window.addEventListener("focus", refresh);
    window.addEventListener(ENTITLEMENTS_INVALIDATED_EVENT, refresh);
    const delay = nextGrantAt ? new Date(nextGrantAt).getTime() - Date.now() : 0;
    const timer = delay > 0 && delay <= 2_147_483_647 ? window.setTimeout(refresh, delay + 1_000) : undefined;
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener(ENTITLEMENTS_INVALIDATED_EVENT, refresh);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [nextGrantAt, refreshEntitlements]);

  if (isLoading) {
    return <ChesslaLoadingMark fullScreen label="Checking your account" />;
  }

  if (!isAuthenticated) {
    return <Navigate to={loginUrl(`${location.pathname}${location.search}${location.hash}`)} replace />;
  }

  if (userId && (settingsOwnerUserId !== userId || settingsLoading)) {
    return <ChesslaLoadingMark fullScreen label="Loading your settings" />;
  }

  return <Outlet />;
};
